import React, { ReactNode } from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { DataTitle } from "../DataTitle";
import { DataCell, Label, RightContent } from "../DataCell";
import { ComparisonSection } from "./ComparisonSection";
import {
  COLOR_CONSTANTS,
  SPACING_CONSTANTS,
} from "../../constants/styles/index";

export interface DataSection {
  id: string;
  title: string;
  number?: number;
  items: {
    label: string;
    value: string | ReactNode;
    unit: string;
    valueColor?: string;
    fontWeight?: number;
    textAlign?: "left" | "center" | "right";
    /**
     * 값이 잘렸을 때 hover 팝업으로 보여줄 본문.
     * `value` 가 문자열이면 생략해도 그 값이 쓰인다 — 노드일 때만 필요하다.
     */
    valueTooltipText?: string;
    /**
     * 라벨이 잘렸을 때 hover 팝업으로 보여줄 본문.
     * `label` 자신이 기본 본문이므로, 라벨과 다른 내용을 띄울 때만 준다.
     */
    labelTooltipText?: string;
  }[];
  alternatingPattern: "grey50" | "grey100";
}

export interface ComparisonData {
  firstNumber: number;
  secondNumber: number;
  items: {
    label: string;
    value: string | ReactNode;
    unit: string;
    valueColor?: string;
    fontWeight?: number;
    textAlign?: "left" | "center" | "right";
    /**
     * 값이 잘렸을 때 hover 팝업으로 보여줄 본문.
     * `value` 가 문자열이면 생략해도 그 값이 쓰인다 — 노드일 때만 필요하다.
     */
    valueTooltipText?: string;
    /**
     * 라벨이 잘렸을 때 hover 팝업으로 보여줄 본문.
     * `label` 자신이 기본 본문이므로, 라벨과 다른 내용을 띄울 때만 준다.
     */
    labelTooltipText?: string;
  }[];
}

/**
 * 선언적 데이터 목록 컴포넌트 — `DataSection` 배열을 받아 `DataTitle` + `DataCell` 로 자동 조립.
 *
 * 개별 행을 조건부로 다루고 싶으면 `DataCell` 을 직접 조립한다.
 */
interface DataListProps {
  /** 섹션 배열 — 각 섹션은 제목 + items 목록을 가진다 */
  sections: DataSection[];
  /** 비교 데이터 — 있으면 별도 `ComparisonSection` 으로 렌더링 */
  comparison?: ComparisonData;
  sx?: SxProps<Theme>;
  width?: string | number;
  minHeight?: string | number;
}

export const DataList: React.FC<DataListProps> = ({
  sections,
  comparison,
  sx,
  width = "100%",
  minHeight,
}) => {
  const getBackgroundColor = (pattern: "grey50" | "grey100", index: number) => {
    if (index % 2 === 0) return "#ffffff";
    return pattern === "grey50"
      ? COLOR_CONSTANTS.COLOR.GREY[50]
      : COLOR_CONSTANTS.COLOR.GREY[100];
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: width,
        minHeight: minHeight || "auto",
        border: `1px solid ${COLOR_CONSTANTS.COLOR.GREY[300]}`,
        borderRadius: `${SPACING_CONSTANTS.spacing[1]}px`,
        overflow: "hidden",
        backgroundColor: "#ffffff",
        padding: `${SPACING_CONSTANTS.spacing[3]}px`,
        ...sx,
      }}
    >
      {sections.map((section, sectionIndex) => (
        <React.Fragment key={section.id}>
          <DataTitle title={section.title} number={section.number} />
          {section.items.map((item, index) => (
            <DataCell
              key={`${section.id}-${index}`}
              sx={{
                backgroundColor: getBackgroundColor(
                  section.alternatingPattern,
                  index
                ),
                padding: `0 ${SPACING_CONSTANTS.spacing[2]}px`,
              }}
            >
              <DataCell.Left>
                <Label tooltipText={item.labelTooltipText}>{item.label}</Label>
              </DataCell.Left>
              <DataCell.Right>
                <RightContent
                  mainText={item.value}
                  subText={item.unit}
                  valueColor={item.valueColor}
                  fontWeight={item.fontWeight}
                  textAlign={item.textAlign}
                  tooltipText={item.valueTooltipText}
                  widthFull={true}
                />
              </DataCell.Right>
            </DataCell>
          ))}
          {sectionIndex < sections.length - 1 && (
            <Box sx={{ height: `${SPACING_CONSTANTS.spacing[5]}px` }} />
          )}
        </React.Fragment>
      ))}

      {comparison && (
        <ComparisonSection
          firstNumber={comparison.firstNumber}
          secondNumber={comparison.secondNumber}
          items={comparison.items}
        />
      )}
    </Box>
  );
};

export type { DataListProps };
